import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Building2, Truck, FileText, BarChart3, ArrowRight, X } from 'lucide-react';
import axios from 'axios';
import { API_BASE as API } from '../config';

export default function WelcomeModal() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  const DEMO_EMAILS = ['demo@allaboutclaims.com', 'demo-provider@allaboutclaims.com'];

  useEffect(() => {
    if (user && !user.onboarding_completed && !user.is_platform_operator && !DEMO_EMAILS.includes(user.email)) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, [user]);

  if (!visible) return null;

  const dismiss = async () => {
    setVisible(false);
    try {
      await axios.put(`${API}/auth/onboarding-status`, { onboarding_completed: true }, { withCredentials: true });
      await checkAuth();
    } catch (err) {
      // Non-blocking: modal already dismissed locally
      console.debug('onboarding-status persistence failed:', err?.message);
    }
  };

  const isProvider = user?.account_type === 'provider';

  const actions = isProvider
    ? [
        { icon: Building2, title: 'Create an Organization', desc: 'Group your client companies', color: 'blue', path: '/organizations' },
        { icon: Truck, title: 'Add a Company', desc: 'Set up a fleet client', color: 'teal', path: '/companies' },
        { icon: FileText, title: 'File a Claim', desc: 'Create your first entry', color: 'amber', path: '/intake' },
        { icon: BarChart3, title: 'Explore Dashboard', desc: 'See stats and charts', color: 'gray', path: '/', action: dismiss },
      ]
    : [
        { icon: Truck, title: 'Add a Company', desc: 'Set up the fleet you manage', color: 'teal', path: '/companies' },
        { icon: FileText, title: 'File a Claim', desc: 'Create your first entry', color: 'amber', path: '/intake' },
        { icon: BarChart3, title: 'Explore Dashboard', desc: 'See stats and charts', color: 'gray', path: '/', action: dismiss },
      ];

  const colorMap = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', hover: 'hover:bg-blue-100' },
    teal: { bg: 'bg-teal-50', icon: 'text-teal-600', hover: 'hover:bg-teal-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', hover: 'hover:bg-amber-100' },
    gray: { bg: 'bg-gray-50', icon: 'text-gray-600', hover: 'hover:bg-gray-100' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md animate-fadeSlideIn" data-testid="welcome-modal">
        <button onClick={dismiss} className="absolute right-3 top-3 p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-testid="welcome-modal-close">
          <X className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-2">
          <h2 className="text-lg font-bold text-gray-900" data-testid="welcome-modal-title">Welcome, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-sm text-gray-500 mt-1">Here's how to get started:</p>
        </div>

        <div className="px-6 pb-4 space-y-2">
          {actions.map((a, i) => {
            const cc = colorMap[a.color];
            return (
              <button
                key={i}
                type="button"
                onClick={() => { dismiss(); if (a.action) a.action(); else navigate(a.path); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 ${cc.hover} transition-colors text-left group`}
                data-testid={`welcome-action-${i}`}
              >
                <div className={`p-2 rounded-lg ${cc.bg}`}>
                  <a.icon className={`w-5 h-5 ${cc.icon}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                  <p className="text-xs text-gray-500">{a.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="px-6 pb-5">
          <Button variant="outline" className="w-full h-10 text-sm" onClick={dismiss} data-testid="welcome-modal-dismiss">
            Got it, let me explore
          </Button>
        </div>
      </div>
    </div>
  );
}
